import { useState } from 'react'
import { apiAgentGetWinLossReport, ApiRequestError } from '@/lib/api'
import type { WinLossReport } from '@nexbit/types'
import { formatCurrency } from '@/lib/utils'
import { format, startOfMonth, endOfDay } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, Search, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'

function toDateInputValue(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

function pct(rate: string): string {
  return `${(parseFloat(rate) * 100).toFixed(0)}%`
}

function signed(value: string) {
  return parseFloat(value) >= 0
    ? 'text-success font-semibold'
    : 'text-destructive font-semibold'
}

export default function MasterReportsPage() {
  const t = useT()
  const [from, setFrom] = useState(toDateInputValue(startOfMonth(new Date())))
  const [to, setTo] = useState(toDateInputValue(endOfDay(new Date())))

  const [rows, setRows] = useState<WinLossReport[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const data = await apiAgentGetWinLossReport({
        from: new Date(from).toISOString(),
        to: new Date(to + 'T23:59:59').toISOString(),
      })
      setRows(data)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }

  const totals = rows?.reduce(
    (acc, r) => ({
      bet: acc.bet + parseFloat(r.totalBetAmount),
      win: acc.win + parseFloat(r.totalWinAmount),
      profit: acc.profit + parseFloat(r.netProfit),
      agentNet: acc.agentNet + parseFloat(r.agentNet),
      masterNet: acc.masterNet + parseFloat(r.masterNet),
      adminNet: acc.adminNet + parseFloat(r.adminNet),
      rounds: acc.rounds + r.roundCount,
      members: acc.members + r.memberCount,
    }),
    { bet: 0, win: 0, profit: 0, agentNet: 0, masterNet: 0, adminNet: 0, rounds: 0, members: 0 },
  )

  const inputClass = cn(
    'h-9 rounded-[var(--radius-sm)] border border-input bg-transparent px-3',
    'font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
  )

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <div className="mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {t.reports.winLoss}
        </span>
      </div>

      <form onSubmit={(e) => void handleSearch(e)}>
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)] mb-4">
          <div className="px-4 py-2.5 bg-secondary">
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
              {t.common.filter}
            </span>
          </div>
          <div className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {t.reports.from}
              </Label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                required
                className={inputClass}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {t.reports.to}
              </Label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                required
                className={inputClass}
              />
            </div>
            <Button type="submit" size="sm" className="h-9 font-mono text-xs gap-1.5" disabled={loading}>
              <Search className="h-3.5 w-3.5" />
              {t.common.search}
            </Button>
          </div>
        </div>
      </form>

      {error && (
        <div
          className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden">
          <div className="px-4 py-2.5 bg-secondary">
            <Skeleton className="h-3 w-24" />
          </div>
          <div className="divide-y divide-border">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex gap-4 px-4 py-3">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>
      ) : rows !== null ? (
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
          <div className="px-4 py-2.5 bg-secondary flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5 text-secondary-foreground/60" />
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
              {t.reports.results}
            </span>
            <span className="font-mono text-xs text-secondary-foreground/60">
              {rows.length} {t.reports.agent}
            </span>
          </div>

          {rows.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-mono text-sm text-muted-foreground">{t.reports.noResults}</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.reports.agent}</TableHead>
                    <TableHead className="text-right">{t.reports.agentShare}</TableHead>
                    <TableHead className="text-right">{t.reports.masterShare}</TableHead>
                    <TableHead className="text-right">{t.reports.betVolume}</TableHead>
                    <TableHead className="text-right">{t.reports.payout}</TableHead>
                    <TableHead className="text-right">{t.reports.netProfit}</TableHead>
                    <TableHead className="text-right">{t.reports.agentNet}</TableHead>
                    <TableHead className="text-right">{t.reports.masterNet}</TableHead>
                    <TableHead className="text-right">{t.reports.adminNet}</TableHead>
                    <TableHead className="text-right">{t.reports.roundCount}</TableHead>
                    <TableHead className="text-right">{t.reports.memberCount}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.partnerId ?? r.partnerCode}>
                      <TableCell className="font-mono text-sm font-semibold">
                        {r.partnerCode}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {pct(r.agentShareRate)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {pct(r.masterShareRate)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(r.totalBetAmount)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(r.totalWinAmount)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        <span className={signed(r.netProfit)}>{formatCurrency(r.netProfit)}</span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        <span className={signed(r.agentNet)}>{formatCurrency(r.agentNet)}</span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        <span className={signed(r.masterNet)}>{formatCurrency(r.masterNet)}</span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        <span className={signed(r.adminNet)}>{formatCurrency(r.adminNet)}</span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {r.roundCount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {r.memberCount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totals && (
                <div className="px-4 py-3 border-t border-border bg-secondary/20 flex flex-wrap gap-x-6 gap-y-1">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {t.reports.totals}
                  </span>
                  <span className="font-mono text-xs">
                    {t.reports.betVolume}: <strong>{formatCurrency(String(totals.bet.toFixed(2)))}</strong>
                  </span>
                  <span className="font-mono text-xs">
                    {t.reports.payout}: <strong>{formatCurrency(String(totals.win.toFixed(2)))}</strong>
                  </span>
                  <span className="font-mono text-xs">
                    {t.reports.netProfit}:{' '}
                    <strong className={totals.profit >= 0 ? 'text-success' : 'text-destructive'}>
                      {formatCurrency(String(totals.profit.toFixed(2)))}
                    </strong>
                  </span>
                  <span className="font-mono text-xs">
                    {t.reports.agentNet}:{' '}
                    <strong className={totals.agentNet >= 0 ? 'text-success' : 'text-destructive'}>
                      {formatCurrency(String(totals.agentNet.toFixed(2)))}
                    </strong>
                  </span>
                  <span className="font-mono text-xs">
                    {t.reports.masterNet}:{' '}
                    <strong className={totals.masterNet >= 0 ? 'text-success' : 'text-destructive'}>
                      {formatCurrency(String(totals.masterNet.toFixed(2)))}
                    </strong>
                  </span>
                  <span className="font-mono text-xs">
                    {t.reports.adminNet}:{' '}
                    <strong className={totals.adminNet >= 0 ? 'text-success' : 'text-destructive'}>
                      {formatCurrency(String(totals.adminNet.toFixed(2)))}
                    </strong>
                  </span>
                  <span className="font-mono text-xs">
                    {t.reports.roundCount}: <strong>{totals.rounds.toLocaleString()}</strong>
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="border border-dashed border-border rounded-[var(--radius)] py-12 text-center">
          <p className="font-mono text-sm text-muted-foreground">{t.reports.noData}</p>
        </div>
      )}
    </div>
  )
}
